import { Box, Button, Collapse, FormLabel, Grid } from '@mui/material';
import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import MiniTable, { IColumn, TableType } from '../../components/table/mini-table';
import { format, isAfter, subYears } from 'date-fns';
import { FormikContextType, FormikHelpers, useFormik } from 'formik';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { FC, useState, useEffect } from 'react';
import { MotoringClaim } from '@acorn-portal/shared/src/services/api';
import { getMotoringClaimsSchema } from '@acorn-portal/shared/src/utils/validation/MotoringClaimsSchema';
import { ISO_UTC_FORMAT } from '@acorn-portal/shared/src/utils/constants';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { COST_OF_CLAIM } from '@acorn-portal/shared/src/utils/modalText';
import { useFormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

interface IDateObject {
	day: string;
	month: string;
	year: string;
}

interface IMotoringClaim {
	dateOfClaim: IDateObject;
	typeOfClaim: string;
	statusOfClaim: string;
	atFault: string;
	noClaimsBonusAffected: string;
	costOfClaim: string;
}
interface IProps {
	data?: MotoringClaim[] | null;
	hasMotoringClaims: boolean;
	onChange: (value: MotoringClaim[]) => void;
	checkDateOfClaim: (dateOfClaim: Date, childFormikHelper: FormikHelpers<IMotoringClaim>) => boolean;
	isAdditionalDriver?: boolean;
}

function minTwoDigits(n: number) {
	return (n < 10 ? '0' : '') + n.toString();
}

const MotoringClaimForm: FC<IProps> = ({
	data,
	hasMotoringClaims,
	onChange,
	checkDateOfClaim,
	isAdditionalDriver = false,
}) => {
	const lists = useAppSelector((redux) => redux.referenceData);
	const [selectedItemIndex, setSelectedItemIndex] = useState<number>();
	const [isOpenModal, setIsOpenModal] = useState(false);
	const [MotoringClaims, setMotoringClaims] = useState<MotoringClaim[]>(data || []);
	const [showForm, setShowForm] = useState(MotoringClaims.length === 0);

	const GenerateDate = (date: IDateObject) => {
		return new Date(Number.parseInt(date.year, 10), Number.parseInt(date.month, 10) - 1, Number.parseInt(date.day, 10));
	};
	const getFormDate = (value?: string) => {
		if (!value) {
			return { year: '', month: '', day: '' };
		}
		const tempDate = new Date(value);
		const year = tempDate.getFullYear();
		const month = tempDate.getMonth();
		const day = tempDate.getDate();

		return {
			year: year.toString(),
			month: minTwoDigits(month + 1),
			day: minTwoDigits(day),
		};
	};

	const handleSubmit = (values: IMotoringClaim, formikHelpers: FormikHelpers<IMotoringClaim>) => {
		const dateOfClaim = GenerateDate(values.dateOfClaim);
		if (isAfter(dateOfClaim, new Date())) {
			formikHelpers.setErrors({
				dateOfClaim: {
					day: "The date of claim can't be in the future.",
				},
			});
			return;
		}
		if (checkDateOfClaim(dateOfClaim, formikHelpers)) return;
		const item: MotoringClaim = {
			dateOfClaim: format(dateOfClaim, ISO_UTC_FORMAT),
			typeOfClaim: values.typeOfClaim,
			statusOfClaim: values.statusOfClaim,
			atFault: values.atFault === 'yes',
			costOfClaim: Number.parseFloat(values.costOfClaim),
			noClaimsBonusAffected: values.noClaimsBonusAffected === 'yes',
		};
		if (selectedItemIndex !== undefined && selectedItemIndex >= 0) {
			const tempArray: MotoringClaim[] = [...MotoringClaims];
			tempArray[selectedItemIndex] = { ...item };
			setMotoringClaims(tempArray);
			setSelectedItemIndex(undefined);
			setShowForm(false);
			formikHelpers.resetForm();
			return;
		}
		const newArray = [...MotoringClaims, item];
		setMotoringClaims(newArray);
		onChange(newArray);
		setShowForm(false);
		formikHelpers.resetForm();
	};

	const formik = useFormik({
		initialValues: {
			dateOfClaim: {
				day: '',
				month: '',
				year: '',
			},
			typeOfClaim: '',
			statusOfClaim: '',
			atFault: '',
			noClaimsBonusAffected: '',
			costOfClaim: '',
		},
		validationSchema: getMotoringClaimsSchema(isAdditionalDriver),
		onSubmit: handleSubmit,
	});

	useFormikLogErrors(formik as FormikContextType<unknown>);

	useEffect(() => {
		if (!hasMotoringClaims) {
			const newArray: MotoringClaim[] = [];
			setMotoringClaims(newArray);
			onChange(newArray);
			formik.resetForm();
		}
	}, [hasMotoringClaims]);

	const GetValue = (x: string) => (lists.claimType.find((item) => item.value === x)?.label as string) || x;

	const COLUMNS = [
		{
			title: 'Claim',
			field: 'typeOfClaim',
			render: (rowData) => GetValue(rowData.typeOfClaim),
		},
		{
			title: 'Value',
			field: 'value',
			render: (rowData) => formatCurrency(rowData.costOfClaim),
		},
	] as IColumn<MotoringClaim>[];

	const onEdit = (item: MotoringClaim, index: number) => {
		formik.setFormikState((state) => ({
			...state,
			values: {
				dateOfClaim: getFormDate(item.dateOfClaim),
				typeOfClaim: item.typeOfClaim,
				statusOfClaim: item.statusOfClaim,
				atFault: item.atFault ? 'yes' : 'no',
				noClaimsBonusAffected: item.noClaimsBonusAffected ? 'yes' : 'no',
				costOfClaim: item.costOfClaim.toString(),
			},
		}));
		setSelectedItemIndex(index);
		setShowForm(true);
	};

	const onRemove = (_item: MotoringClaim, index: number) => {
		setSelectedItemIndex(index);
		setIsOpenModal(true);
	};

	const onDelete = () => {
		if (selectedItemIndex === undefined) {
			setIsOpenModal(false);
			return;
		}
		const newArray = [...MotoringClaims];
		newArray.splice(selectedItemIndex, 1);
		if (newArray.length === 0) {
			formik.resetForm();
			setShowForm(true);
		}
		setMotoringClaims(newArray);
		onChange(newArray);
		setIsOpenModal(false);
		setSelectedItemIndex(undefined);
	};

	return (
		<>
			<Box
				sx={(theme) => ({
					pr: '20%',
					flexDirection: 'column',
					justifyContent: 'space-around',
					[theme.breakpoints.down('md')]: {
						pr: 'unset',
					},
				})}
			>
				{MotoringClaims.length > 0 && (
					<Box>
						<Grid container direction='row' spacing={1}>
							<Grid
								size={{ xs: 12, md: 6 }}
								sx={(theme) => ({
									textAlign: 'right',
									[theme.breakpoints.down('md')]: { textAlign: 'unset' },
								})}
							>
								<FormLabel>Motoring Claims</FormLabel>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<MiniTable
									data={MotoringClaims}
									columns={COLUMNS}
									onRemove={onRemove}
									onEdit={onEdit}
									isEditing={showForm}
									tableType={TableType.CLAIMS}
								/>
								<Collapse in={!showForm}>
									<Box display='flex' justifyContent='flex-end'>
										<Button
											sx={(theme) => ({
												my: 2,
												[theme.breakpoints.down('md')]: {
													width: '100%',
												},
											})}
											variant='contained'
											color='primary'
											onClick={() => setShowForm(true)}
										>
											Add Another Claim
										</Button>
									</Box>
								</Collapse>
							</Grid>
						</Grid>
					</Box>
				)}
			</Box>
			<Collapse in={showForm}>
				{showForm && (
					<Box
						sx={(theme) => ({
							backgroundColor: theme.palette.grey[300],
							borderRadius: 2,
							my: 2,
							p: 2,
							pt: 3,
							'& .MuiOutlinedInput-root': {
								backgroundColor: theme.palette.common.white,
							},
						})}
					>
						<DatePickerField
							id='dateOfClaim'
							onChange={(value) => formik.setFieldValue('dateOfClaim', value)}
							label='Date of claim'
							minYear={subYears(new Date(), 5).getFullYear()}
							value={formik.values.dateOfClaim}
							error={
								(formik.touched.dateOfClaim?.day && Boolean(formik.errors.dateOfClaim?.day)) ||
								(formik.touched.dateOfClaim?.month && Boolean(formik.errors.dateOfClaim?.month)) ||
								(formik.touched.dateOfClaim?.year && Boolean(formik.errors.dateOfClaim?.year))
							}
							helperText={
								(formik.touched.dateOfClaim?.day && formik.errors.dateOfClaim?.day) ||
								(formik.touched.dateOfClaim?.month && formik.errors.dateOfClaim?.month) ||
								(formik.touched.dateOfClaim?.year && formik.errors.dateOfClaim?.year)
							}
						/>
						<SelectFormField
							label='Claim Type'
							name='typeOfClaim'
							id='typeOfClaim'
							value={formik.values.typeOfClaim}
							onChange={(value) => formik.setFieldValue('typeOfClaim', value)}
							options={lists.claimType as IOptions[]}
							error={formik.touched.typeOfClaim && Boolean(formik.errors.typeOfClaim)}
							helperText={formik.touched.typeOfClaim && formik.errors.typeOfClaim}
						/>

						<SelectFormField
							label='Status of claim'
							name='statusOfClaim'
							id='statusOfClaim'
							value={formik.values.statusOfClaim}
							onChange={(value) => formik.setFieldValue('statusOfClaim', value)}
							options={lists.claimStatues as IOptions[]}
							error={formik.touched.statusOfClaim && Boolean(formik.errors.statusOfClaim)}
							helperText={formik.touched.statusOfClaim && formik.errors.statusOfClaim}
						/>
						<RadioGroupButton
							label='Were they at fault?'
							name='atFault'
							id='atFault'
							value={formik.values.atFault}
							onChange={(value) => formik.setFieldValue('atFault', value)}
							error={formik.touched.atFault && Boolean(formik.errors.atFault)}
							helperText={formik.touched.atFault && formik.errors.atFault}
						/>
						<RadioGroupButton
							label='Was their No Claims Bonus affected?'
							name='noClaimsBonusAffected'
							id='noClaimsBonusAffected'
							value={formik.values.noClaimsBonusAffected}
							onChange={(value) => formik.setFieldValue('noClaimsBonusAffected', value)}
							error={formik.touched.noClaimsBonusAffected && Boolean(formik.errors.noClaimsBonusAffected)}
							helperText={formik.touched.noClaimsBonusAffected && formik.errors.noClaimsBonusAffected}
						/>
						<FormTextField
							id='costOfClaim'
							name='costOfClaim'
							label='Cost of claim (£)'
							error={formik.touched.costOfClaim && Boolean(formik.errors.costOfClaim)}
							helperText={formik.touched.costOfClaim && formik.errors.costOfClaim}
							modal={{
								title: COST_OF_CLAIM.header,
								description: COST_OF_CLAIM.text,
							}}
							onChange={formik.handleChange}
							value={formik.values.costOfClaim}
							format='currency'
						/>
						<Box
							display='flex'
							justifyContent='flex-end'
							sx={(theme) => ({
								pr: '20%',
								[theme.breakpoints.down('md')]: {
									pr: 'unset',
								},
							})}
						>
							<Button
								sx={(theme) => ({
									my: 2,
									mr: 2,
									minWidth: '10%',
									[theme.breakpoints.down('md')]: {
										width: '50%',
									},
								})}
								variant='contained'
								color='secondary'
								disabled={MotoringClaims.length === 0}
								onClick={() => {
									setShowForm(false);
									formik.resetForm();
									setSelectedItemIndex(undefined);
								}}
							>
								Cancel
							</Button>
							<Button
								sx={(theme) => ({
									my: 2,
									minWidth: '100px',
									[theme.breakpoints.down('md')]: {
										width: '50%',
									},
								})}
								variant='contained'
								color='primary'
								onClick={() => formik.handleSubmit()}
							>
								{selectedItemIndex !== undefined && Number(selectedItemIndex) >= 0 ? 'Edit' : 'Add'}
							</Button>
						</Box>
					</Box>
				)}
			</Collapse>
			<HelperModal
				open={isOpenModal}
				onClose={() => {
					setSelectedItemIndex(undefined);
					setIsOpenModal(false);
				}}
				title='Remove Claim'
				description='Are you sure you want to remove the claims?'
				closeText='Cancel'
				submitText='Delete'
				onSubmit={() => onDelete()}
			/>
		</>
	);
};

export default MotoringClaimForm;
